import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import config from 'constants/config'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import GeoContext from 'contexts/geoContext'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import ContactContext from 'contexts/contactContext'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'

interface Props {
  supportAssistantEnabled: boolean
  className?: string
  onClick: () => void
}

function PhoneNumberMobileMenu({
  supportAssistantEnabled,
  onClick,
  className,
}: Props) {
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const localContact = currentContact.local
  const internationalContact = currentContact.international
  const { currentRegionCode } = useContext(GeoContext)
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  return (
    <div className={className}>
      {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
        <ClickableListItem
          title={intl.formatMessage({
            id: 'n+Zw7I5xya',
            defaultMessage: 'Local Sales & Support',
            description: 'Title for local sales and support contact in mobile phone menu',
          })}
          subtitle={localContact.humanReadable}
          startIcon={<LinePhoneIcon />}
          href={`tel:${localContact.number}`}
          aria-label={intl.formatMessage({
            id: 'LP65m9AA5F',
            defaultMessage: 'Local Sales & Support contact',
            description: 'Aria label for local sales and support contact in mobile phone menu',
          })}
          rel="nofollow"
        />
      )}
      {supportAssistantEnabled && <ClickableListItem
        title={intl.formatMessage({
          id: 'kmdmb91EdF',
          defaultMessage: 'Chat now',
          description: 'Title for chat support option in mobile phone menu',
        })}
        subtitle={intl.formatMessage({
          id: 'M4IF7X8Li4',
          defaultMessage: 'Luxury Escapes Support Assistant',
          description: 'Subtitle for chat support in mobile phone menu in the site header',
        })}
        startIcon={<LineCommentsIcon />}
        to="/support"
        onClick={onClick}
        aria-label={intl.formatMessage({
          id: 'v/CPVwLlj4',
          defaultMessage: 'Luxury Escapes Support Assistant',
          description: 'Aria label for chat support in mobile phone menu element',
        })}
        rel="nofollow"
      />}
      {shouldRenderWhatsApp &&
        <ClickableListItem
          title={intl.formatMessage({
            id: 'hkLBlFVBZL',
            defaultMessage: 'WhatsApp',
            description: 'Title for WhatsApp contact option in mobile navigation menu',
          })}
          startIcon={<img
            src={getSVGImageUrl('whatsapp-logo')}
            width="24"
            height="24"
            alt={intl.formatMessage({
              id: 'ECNjAesoTC',
              defaultMessage: 'WhatsApp',
              description: 'Alt text for WhatsApp logo in mobile navigation menu',
            })}
          />}
          href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
          aria-label={intl.formatMessage({
            id: 'nIOwOa9rIe',
            defaultMessage: 'WhatsApp',
            description: 'Accessibility label for WhatsApp contact button in mobile navigation menu',
          })}
          rel="noopener"
          target="_blank"
        />}
      <ClickableListItem
        title={intl.formatMessage({
          id: 'rns/PEP912',
          defaultMessage: 'International Support',
          description: 'Title for international support contact in mobile phone menu',
        })}
        startIcon={config.BRAND === 'luxuryescapes' ? <LinePhoneIcon /> : undefined}
        subtitle={internationalContact.humanReadable}
        href={`tel:${internationalContact.number}`}
        aria-label={intl.formatMessage({
          id: 'DWNebmRa0Z',
          defaultMessage: 'International Support contact',
          description: 'Aria label for international support contact in mobile phone menu',
        })}
        rel="nofollow"
      />
    </div>
  )
}

function mapStateToProps(state: App.State) {
  return {
    supportAssistantEnabled: isSupportAssistantEnabled(state),
  }
}

export default connect(mapStateToProps)(PhoneNumberMobileMenu)
